/**
 * Mapping path → permission (Порція 3.0.5.5).
 *
 * Використовується автоматичним route-guard wrapper'ом у `App.tsx`:
 * для кожного path знаходимо потрібний permission і огортаємо
 * `<RouteGuard>`. Це масштабованіше, ніж обгортати 117 routes вручну.
 *
 * Path-pattern → permission mapping. Враховує:
 *   - Точне співпадіння (`/admin`).
 *   - Префікс зі шматком `/:id` (`goods-sales/:id`, `goods-sales/:id/print`).
 *   - Базовий префікс модулю (`/reports/...` → одне правило).
 *
 * NULL у мапі означає «без guard'а» — публічно для будь-кого з tenant.
 * Це для нейтральних сторінок (home, dashboard).
 */

/** Path-prefix → permission. */
const RULES: Array<[RegExp, string | readonly string[] | null]> = [
  // Home — без guard.
  [/^\/?$/, null],

  // Admin / settings / команда — критичні permissions
  [/^admin$/, 'admin.access.manage'],
  [/^team$/, 'admin.users.manage'],
  [/^organizations$/, 'admin.organizations.manage'],
  [/^settings\//, 'admin.settings.manage'],
  [/^audit-log$/, 'admin.access.manage'],

  // Refs (всі довідники — Порція A: гранулярні per-довідник)
  [/^contractors$/, 'refs.contractors.view'],
  // ч.1094: дублі контрагентів — бачить той, хто редагує довідник.
  [/^contractors\/duplicates$/, 'refs.contractors.edit'],
  // ч.1059: /nomenclature/new і /nomenclature/:id — картка окремою сторінкою.
  [/^nomenclature(\/|$)/, 'refs.nomenclature.view'],
  // /departments: узгоджено з backend (permission_enforcer.py:232
  // → payroll.view). Концептуально «departments це refs чи payroll» —
  // у TECHDEBT, рішення з власником продукту.
  [/^departments$/, 'payroll.view'],

  // Accounting
  [/^chart-of-accounts$/, 'accounting.view'],
  [/^manual-operations/, 'accounting.view'],
  [/^debt-adjustments/, 'accounting.view'],
  [/^doubtful-debt-reserves/, 'accounting.view'],
  [/^fx-revaluations/, 'accounting.view'],
  [/^closing\/month$/, 'accounting.period.close'],
  // ч.1098: аудит проводок.
  [/^audit\/postings/, 'accounting.audit.view'],

  // Sales
  [/^goods-sales/, 'sales.view'],
  [/^customer-orders/, 'sales.view'],
  [/^goods-returns-from-customer/, 'sales.view'],

  // Purchases
  [/^goods-receipts/, 'purchases.view'],
  // ч.1093: вхідні документи (розпізнавання) — полка і перевірка.
  [/^incoming-documents/, 'recognition.view'],
  [/^supplier-orders/, 'purchases.view'],
  [/^goods-returns-to-supplier/, 'purchases.view'],
  [/^customs-declarations/, 'purchases.view'],
  [/^additional-expenses/, 'purchases.view'],

  // Warehouse
  [/^goods-transfers/, 'warehouse.view'],
  [/^inventory-counts/, 'warehouse.view'],
  [/^goods-recognitions/, 'warehouse.view'],
  [/^goods-writeoffs/, 'warehouse.view'],

  // Cash
  [/^cash-receipts/, 'cash.view'],
  [/^cash-payments/, 'cash.view'],
  [/^cash-summary$/, 'cash.view'],
  [/^advance-reports/, 'cash.view'],

  // Bank
  [/^bank-receipts/, 'bank.view'],
  [/^bank-payments/, 'bank.view'],
  [/^bank-summary$/, 'bank.view'],
  [/^bank-import$/, 'bank.view'],

  // Tax
  [/^tax-invoices-outgoing/, 'tax.view'],
  [/^tax-invoices-incoming/, 'tax.view'],

  // Payroll
  [/^payrolls/, 'payroll.view'],
  [/^payroll-payments/, 'payroll.view'],
  [/^timesheets/, 'payroll.view'],
  [/^employees/, 'payroll.view'],
  // ч.1059: /individuals/new і /individuals/:id — картка окремою сторінкою.
  [/^individuals(\/|$)/, 'payroll.view'],
  [/^positions$/, 'payroll.view'],
  [/^profession-classifier$/, 'payroll.view'],
  [/^hire-orders/, 'payroll.view'],
  [/^staff-transfers/, 'payroll.view'],
  [/^dismissal-orders/, 'payroll.view'],

  // ФОП (Block 8 single_tax + Block 11 fop_zso). Захист routes від MEMBER
  // що прямим URL переходить на /fop/* без необхідного permission'у.
  // Знахідка від audit 2026-06-04 (multi-agent workflow): App.tsx маршрути
  // 501-505 існували без routePermissions mapping, AutoRouteGuard
  // пропускав їх через `default: null` поведінку.
  [/^fop\/single-tax/, 'documents.single_tax_calculation.manage'],
  [/^fop\/self-esv/, 'documents.fop_self_esv_payment.manage'],
  [/^fop\/self-wz/, 'documents.fop_self_wz_payment.manage'],
  [/^fop\/reports/, 'refs.fop_reports.view'],
  [/^fop-zso\/quarterly/, 'documents.fop_zso_quarterly_calculation.manage'],

  // Assets — Підпорція 1.5: legacy patterns видалено.
  // Replacement у /assets/* (1.1-1.4) — окремий routePermission не потрібен,
  // permission checks через usePermissionsByOrg на самих pages.

  // Reports (regulated)
  [/^reports\/trial-balance$/, 'reports.regulated.view'],
  [/^reports\/goods-balance$/, 'reports.regulated.view'],
  [/^reports\/goods-reserved$/, 'reports.regulated.view'],
  [/^reports\/account-card$/, 'reports.regulated.view'],
  [/^reports\/subconto-analysis$/, 'reports.regulated.view'],
  [/^reports\/reconciliation-act/, 'reports.regulated.view'],
  [/^reports\/financial$/, 'reports.regulated.view'],
  [/^reports\/tax$/, 'reports.regulated.view'],
  [/^reports\/vat-summary$/, 'reports.regulated.view'],
  [/^reports\/vat-declaration-print$/, 'reports.regulated.view'],

  // Reports (management)
  [/^finance$/, 'reports.management.view'],
  [/^projects$/, 'reports.management.view'],
  [/^reports\/project-pnl$/, 'reports.management.view'],
  [/^reports\/profitability$/, 'reports.management.view'],
  [/^reports\/cash-flow$/, 'reports.management.view'],

  // Import / migration. ч.1083: сторінки міграції — під правом «Міграція з 1С»
  // (те саме, що меню і API майстра); CSV-імпорт — окреме право import.run.
  // ч.1112: чек-ап після міграції — і право перегляду (бухгалтер).
  [/^migration\/checkup$/, ['admin.migration.run', 'migration.checkup.view']],
  [/^migration\//, 'admin.migration.run'],
  // ч.1084: «Початкові дані (без 1С)» = імпорт довідників + вступні залишки
  // на одній сторінці — пускаємо з будь-яким із двох прав (блоки сторінки
  // перевіряють своє право окремо). Редактор документа — лише налаштування.
  [/^import-csv$/, ['import.run', 'admin.settings.manage']],
  [/^opening-balances$/, ['import.run', 'admin.settings.manage']],
  [/^opening-balances\//, 'admin.settings.manage'],
];

/** Знайти потрібний permission для path. null = без обмеження. */
export function permissionForPath(path: string): string | readonly string[] | null {
  const cleaned = path.replace(/^\/+/, '');
  for (const [pattern, perm] of RULES) {
    if (pattern.test(cleaned)) return perm;
  }
  return null;
}
